import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../../../../services';
import { useNotifications } from '../../../../contexts/NotificationContext';
import type { Notification } from '../../../../types/course';
import './Notifications.css';

// ── Helpers ──────────────────────────────────────────────────

const formatRelativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  if (minutes < 1) return 'Adesso';
  if (minutes < 60) return `${minutes} min fa`;
  if (hours < 24) return `${hours} ore fa`;
  if (days < 7) return `${days} giorni fa`;
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const TYPE_CONFIG: Record<
  Notification['type'],
  { icon: string; className: string }
> = {
  info: { icon: 'bi-info-circle-fill', className: 'notif-item--info' },
  success: { icon: 'bi-check-circle-fill', className: 'notif-item--success' },
  warning: {
    icon: 'bi-exclamation-triangle-fill',
    className: 'notif-item--warning',
  },
  course: { icon: 'bi-collection-play-fill', className: 'notif-item--course' },
};

type FilterType = 'all' | 'unread';

// ── NotificationItem ─────────────────────────────────────────

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onDelete: (id: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification: n,
  onMarkRead,
  onDelete,
}) => {
  const navigate = useNavigate();
  const { icon, className } = TYPE_CONFIG[n.type] ?? TYPE_CONFIG.info;

  const handleAction = () => {
    if (!n.read) onMarkRead(n.id);
    if (n.actionUrl?.startsWith('/')) navigate(n.actionUrl);
  };

  return (
    <div
      className={[
        'notif-item',
        className,
        n.read ? 'notif-item--read' : 'notif-item--unread',
      ].join(' ')}
      role="article"
      aria-label={n.read ? n.title : `Non letto: ${n.title}`}
    >
      {!n.read && <span className="notif-item__dot" aria-hidden="true" />}

      <span className="notif-item__icon" aria-hidden="true">
        <i className={`bi ${icon}`} />
      </span>

      <div className="notif-item__content">
        <p className="notif-item__title">{n.title}</p>
        <p className="notif-item__message">{n.message}</p>
        <div className="notif-item__footer">
          <span className="notif-item__time">
            <i className="bi bi-clock me-1" aria-hidden="true" />
            {formatRelativeTime(n.createdAt)}
          </span>
          {n.actionUrl && (
            <button className="notif-item__action" onClick={handleAction}>
              {n.actionLabel ?? 'Vedi'}
              <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="notif-item__actions">
        {!n.read && (
          <button
            className="notif-item__btn"
            onClick={() => onMarkRead(n.id)}
            aria-label="Segna come letto"
            title="Segna come letto"
          >
            <i className="bi bi-check2" aria-hidden="true" />
          </button>
        )}
        <button
          className="notif-item__btn notif-item__btn--delete"
          onClick={() => onDelete(n.id)}
          aria-label="Elimina notifica"
          title="Elimina"
        >
          <i className="bi bi-trash" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState('');

  // refresh() aggiorna il badge nel layout e nella navbar
  const { refresh } = useNotifications();

  useEffect(() => {
    notificationService
      .getAll()
      .then(setNotifications)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const filtered = useMemo(
    () =>
      filter === 'unread'
        ? notifications.filter((n) => !n.read)
        : notifications,
    [notifications, filter]
  );

  const markRead = useCallback(
    async (id: string) => {
      try {
        await notificationService.markRead(id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read: true } : n))
        );
        refresh();
      } catch {
        setActionError('Errore. Riprova.');
      }
    },
    [refresh]
  );

  const markAllRead = useCallback(async () => {
    try {
      await notificationService.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      refresh();
    } catch {
      setActionError('Errore. Riprova.');
    }
  }, [refresh]);

  const deleteOne = useCallback(
    async (id: string) => {
      try {
        await notificationService.deleteOne(id);
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        refresh();
      } catch {
        setActionError("Errore durante l'eliminazione. Riprova.");
      }
    },
    [refresh]
  );

  const clearAll = useCallback(async () => {
    try {
      await notificationService.clearAll();
      setNotifications([]);
      refresh();
    } catch {
      setActionError("Errore durante l'eliminazione. Riprova.");
    }
  }, [refresh]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '40vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="notif-page">
        <div className="notif-empty" role="alert">
          <i className="bi bi-wifi-off notif-empty__icon" aria-hidden="true" />
          <p className="notif-empty__title">
            Impossibile caricare le notifiche
          </p>
          <p className="notif-empty__sub">
            Controlla la connessione e riprova.
          </p>
          <button
            className="notif-action-btn mt-3"
            onClick={() => {
              setLoadError(false);
              setLoading(true);
              notificationService
                .getAll()
                .then(setNotifications)
                .catch(() => setLoadError(true))
                .finally(() => setLoading(false));
            }}
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notif-page">
      {actionError && (
        <div
          className="alert alert-danger alert-dismissible d-flex align-items-center gap-2 mb-3"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill flex-shrink-0" />
          <span>{actionError}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setActionError('')}
            aria-label="Chiudi"
          />
        </div>
      )}
      {/* ── Header ── */}
      <div className="notif-header">
        <div>
          <h1 className="notif-title">Notifiche</h1>
          <p className="notif-subtitle">
            {unreadCount > 0
              ? `${unreadCount} non lett${unreadCount === 1 ? 'a' : 'e'}`
              : 'Tutto letto'}
          </p>
        </div>

        {notifications.length > 0 && (
          <div className="notif-header__actions">
            {unreadCount > 0 && (
              <button className="notif-action-btn" onClick={markAllRead}>
                <i className="bi bi-check2-all me-1" />
                Segna tutto letto
              </button>
            )}
            <button
              className="notif-action-btn notif-action-btn--danger"
              onClick={clearAll}
            >
              <i className="bi bi-trash me-1" />
              Cancella tutto
            </button>
          </div>
        )}
      </div>

      {/* ── Filtri ── */}
      {notifications.length > 0 && (
        <div
          className="notif-filters"
          role="tablist"
          aria-label="Filtro notifiche"
        >
          {(['all', 'unread'] as FilterType[]).map((f) => (
            <button
              key={f}
              role="tab"
              aria-selected={filter === f}
              className={`notif-filter-btn ${filter === f ? 'notif-filter-btn--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'Tutte' : 'Non lette'}
              {f === 'unread' && unreadCount > 0 && (
                <span className="notif-filter-badge">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ── Lista ── */}
      {filtered.length === 0 ? (
        <div className="notif-empty">
          <i
            className="bi bi-bell-slash notif-empty__icon"
            aria-hidden="true"
          />
          <p className="notif-empty__title">
            {filter === 'unread'
              ? 'Nessuna notifica non letta'
              : 'Nessuna notifica'}
          </p>
          <p className="notif-empty__sub">
            {filter === 'unread'
              ? 'Hai letto tutto — ottimo!'
              : 'Le notifiche sui tuoi corsi appariranno qui.'}
          </p>
          {filter === 'unread' && (
            <button
              className="notif-action-btn mt-3"
              onClick={() => setFilter('all')}
            >
              Vedi tutte
            </button>
          )}
        </div>
      ) : (
        <div className="notif-list" role="feed" aria-label="Lista notifiche">
          {filtered.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onMarkRead={markRead}
              onDelete={deleteOne}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
